import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Rocket, Megaphone, Sparkles, TrendingUp, Target, Handshake, Users, ShieldCheck, CalendarClock, Play, RotateCcw,
  type LucideIcon,
} from "lucide-react";
import { useTheme } from "@/providers/ThemeProvider";
import { palettes, resolveVariant, type VariantKey } from "./covers/palette";

const variants = {
  v1: lazy(() => import("./covers/V1")),
  v2: lazy(() => import("./covers/V2")),
  v3: lazy(() => import("./covers/V3")),
  v4: lazy(() => import("./covers/V4")),
  v5: lazy(() => import("./covers/V5")),
  v6: lazy(() => import("./covers/V6")),
  v7: lazy(() => import("./covers/V7")),
  v8: lazy(() => import("./covers/V8")),
  v9: lazy(() => import("./covers/V9")),
} as const;

const variantIcons: Record<VariantKey, LucideIcon> = {
  v1: Rocket, v2: Megaphone, v3: Sparkles, v4: TrendingUp, v5: Target,
  v6: Handshake, v7: Users, v8: ShieldCheck, v9: CalendarClock,
};

export type BootcampCoverProps = {
  slug: string;
  title: string;
  description?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Optional intro video URL; rendered as an overlay with native controls. */
  introVideoSrc?: string;
};

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mql.matches);
    const onChange = () => setReduce(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduce;
}

export function BootcampCover({ slug, title, description, size = "sm", className, introVideoSrc }: BootcampCoverProps) {
  const { resolvedTheme } = useTheme();
  const reduce = usePrefersReducedMotion();
  const variantKey = useMemo(() => resolveVariant(slug), [slug]);
  const Variant = variants[variantKey];
  const Icon = variantIcons[variantKey];
  const palette = palettes[variantKey][resolvedTheme === "dark" ? "dark" : "light"];
  const uid = useMemo(() => `${variantKey}-${slug.replace(/[^a-z0-9]/gi, "")}`, [variantKey, slug]);
  const [playing, setPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const aspect = size === "lg" ? "aspect-[21/9]" : "aspect-video";

  const videoSrc = useMemo(() => {
    if (!introVideoSrc) return undefined;
    if (attempt === 0) return introVideoSrc;
    const sep = introVideoSrc.includes("?") ? "&" : "?";
    return `${introVideoSrc}${sep}retry=${attempt}`;
  }, [introVideoSrc, attempt]);

  const onVideoError = () => {
    // eslint-disable-next-line no-console
    console.warn(`[BootcampCover] intro video failed to load for slug="${slug}"`);
    setVideoFailed(true);
    setPlaying(false);
  };

  const onRetry = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setVideoFailed(false);
    setAttempt((n) => n + 1);
    // Auto-resume on hero; require a fresh Play click on the compact card.
    setPlaying(size !== "sm");
  };

  const svgCover: ReactNode = (
    <>
      <Suspense fallback={null}>
        <Variant title={title} description={description} palette={palette} animate={!reduce} uid={uid} />
      </Suspense>
      <div className="pointer-events-none absolute bottom-3 left-3 flex h-10 w-10 items-center justify-center rounded-xl border border-white/30 bg-white/15 text-white shadow-sm backdrop-blur-md">
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-gradient-to-b from-transparent to-card" />
    </>
  );

  // Detail page (md/lg): render the video directly, exactly like the initial design.
  if (introVideoSrc && size !== "sm") {
    if (videoFailed) {
      return (
        <div
          className={`relative w-full overflow-hidden rounded-t-xl ${aspect} ${className ?? ""}`}
          style={{ background: `linear-gradient(135deg, ${palette.gradStart}, ${palette.gradEnd})` }}
        >
          {svgCover}
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30">
            <button
              type="button"
              onClick={onRetry}
              aria-label={`Retry loading ${title} intro video`}
              className="inline-flex items-center gap-2 rounded-full border border-border-1 bg-card/80 px-4 py-2 t-body-sm text-text-1 shadow-lg backdrop-blur-md transition-colors hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <RotateCcw className="h-4 w-4" />
              Video unavailable — Retry
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={`relative w-full overflow-hidden rounded-t-xl ${aspect} ${className ?? ""}`}>
        <video
          src={videoSrc}
          key={`hero-${attempt}`}
          controls
          preload="metadata"
          playsInline
          onError={onVideoError}
          aria-label={`${title} — intro video`}
          className="absolute inset-0 h-full w-full bg-black object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={`group/cover relative w-full overflow-hidden rounded-t-xl ${aspect} ${className ?? ""}`}
      style={{ background: `linear-gradient(135deg, ${palette.gradStart}, ${palette.gradEnd})` }}
    >
      {svgCover}
      {introVideoSrc && !playing && !videoFailed && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true); }}
          aria-label={`Play ${title} intro video`}
          className="group/play absolute inset-0 z-10 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className={`flex h-16 w-16 items-center justify-center rounded-full border border-white/40 bg-white/15 text-white shadow-lg backdrop-blur-md ${reduce ? "" : "transition-transform duration-200 group-hover/play:scale-110"}`}>
            <Play className="h-7 w-7 translate-x-0.5 fill-current" />
          </span>
        </button>
      )}
      {introVideoSrc && playing && !videoFailed && (
        <video
          src={videoSrc}
          key={`card-${attempt}`}
          controls
          autoPlay
          preload="metadata"
          playsInline
          onError={onVideoError}
          aria-label={`${title} — intro video`}
          className="absolute inset-0 z-20 h-full w-full bg-black object-cover"
        />
      )}
      {introVideoSrc && videoFailed && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30">
          <button
            type="button"
            onClick={onRetry}
            aria-label={`Retry loading ${title} intro video`}
            className="inline-flex items-center gap-2 rounded-full border border-border-1 bg-card/80 px-3 py-1.5 t-body-sm text-text-1 shadow-lg backdrop-blur-md transition-colors hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <RotateCcw className="h-4 w-4" />
            Retry
          </button>
        </div>
      )}
    </div>
  );
}

export default BootcampCover;
